import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Typography, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import CourseAPIClient from "../../APIClients/CourseAPIClient";
import { HOME_PAGE } from "../../constants/Routes";
import { CourseModule } from "../../types/CourseTypes";
import { ExpandCollapseButton } from "../bookmarks";
import { FinishedModulesContent } from "../finished_modules";
import LearnerUnitSidebar from "../learners/HomePageSidebar";
import { useCourseUnits } from "../../contexts/CourseUnitsContext";

const FinishedModules = (): React.ReactElement => {
  const theme = useTheme();
  const history = useHistory();
  const {
    courseUnits: units,
    isModuleCompleted,
    getModuleCompletionDate,
  } = useCourseUnits();
  const [modules, setModules] = useState<{ [unitId: string]: CourseModule[] }>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buttonState, setButtonState] = useState<"expand" | "collapse">(
    "expand",
  );
  const [unitOpenMap, setUnitOpenMap] = useState<Record<string, boolean>>({});
  const [expandAllValue, setExpandAllValue] = useState(false);
  const [expandAllStamp, setExpandAllStamp] = useState(0);

  // Fetch modules for a specific unit
  const fetchModulesForUnit = async (unitId: string) => {
    try {
      const modulesData = await CourseAPIClient.getModules(unitId);
      setModules((prev) => ({
        ...prev,
        [unitId]: modulesData,
      }));
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(`Failed to fetch modules for unit ${unitId}:`, err);
    }
  };

  // Load all data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      // Fetch modules for all units
      await Promise.all(units.map((unit) => fetchModulesForUnit(unit.id)));

      setLoading(false);
    };

    loadData();
  }, [units]);

  // Filter modules to only include completed ones
  const completedModules = useMemo(() => {
    const filtered: { [unitId: string]: CourseModule[] } = {};
    Object.entries(modules).forEach(([unitId, unitModules]) => {
      filtered[unitId] = unitModules.filter((module) =>
        isModuleCompleted(module.id),
      );
    });
    return filtered;
  }, [modules, isModuleCompleted]);

  // Filter units to only show those with completed modules
  const unitsWithCompletedModules = useMemo(() => {
    return units.filter(
      (unit) =>
        completedModules[unit.id] && completedModules[unit.id].length > 0,
    );
  }, [units, completedModules]);

  // Handler to receive unit open state
  const handleUnitOpenStateChange = (unitId: string, isOpen: boolean) => {
    setUnitOpenMap((prev) => ({ ...prev, [unitId]: isOpen }));
  };

  // Check if all units are currently expanded
  const allUnitsExpanded = useMemo(() => {
    if (unitsWithCompletedModules.length === 0) return false;
    const reportedCount = Object.keys(unitOpenMap).length;
    if (reportedCount < unitsWithCompletedModules.length) return false;

    return unitsWithCompletedModules.every(
      (unit) => unitOpenMap[unit.id] === true,
    );
  }, [unitOpenMap, unitsWithCompletedModules]);

  // Check if all units are currently collapsed
  const allUnitsCollapsed = useMemo(() => {
    if (unitsWithCompletedModules.length === 0) return false;
    const reportedCount = Object.keys(unitOpenMap).length;
    if (reportedCount < unitsWithCompletedModules.length) return false;

    return unitsWithCompletedModules.every(
      (unit) => unitOpenMap[unit.id] === false,
    );
  }, [unitOpenMap, unitsWithCompletedModules]);

  // Update button state based on unit states
  useEffect(() => {
    if (allUnitsExpanded) {
      setButtonState("collapse");
    } else if (allUnitsCollapsed) {
      setButtonState("expand");
    }
  }, [allUnitsExpanded, allUnitsCollapsed]);

  // Toggle expand/collapse all (button)
  const handleToggleAll = () => {
    if (buttonState === "collapse") {
      setExpandAllValue(false);
      setExpandAllStamp((s) => s + 1);
      setButtonState("expand");
    } else {
      setExpandAllValue(true);
      setExpandAllStamp((s) => s + 1);
      setButtonState("collapse");
    }
  };

  return (
    <Box
      display="flex"
      width="100%"
      minHeight="100vh"
      height="100vh"
      overflow="hidden"
    >
      {/* Sidebar */}
      <LearnerUnitSidebar />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: "48px", mb: "48px", overflowY: "scroll" }}>
        {!loading && !error && unitsWithCompletedModules.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom="32px"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="12px"
            >
              <Button
                onClick={() => history.push(HOME_PAGE)}
                startIcon={<ArrowBackIcon sx={{ fontSize: "12px" }} />}
                sx={{
                  padding: "12px",
                  color: theme.palette.Learner.Dark.Default,
                }}
              >
                <Typography variant="labelLarge">Back</Typography>
              </Button>
              <Typography variant="displayLarge">Finished Modules</Typography>
            </Box>

            {/* Expand/Collapse all button */}
            <ExpandCollapseButton
              allExpanded={buttonState === "collapse"}
              onToggle={handleToggleAll}
            />
          </Box>
        )}

        <FinishedModulesContent
          units={unitsWithCompletedModules}
          modules={completedModules}
          loading={loading}
          error={error}
          allExpanded={expandAllValue}
          expandAllStamp={expandAllStamp}
          onUnitOpenStateChange={handleUnitOpenStateChange}
          getModuleCompletionDate={getModuleCompletionDate}
        />
      </Box>
    </Box>
  );
};

export default FinishedModules;
